import React, { useEffect, useState } from 'react';
import RepairStatisticsV from './RepairStatistics.v';
import PageUtil from 'utils/Page.util';
import moment from 'moment';
import { withRouter } from 'core/withRouter';
import { dateFormat } from 'utils/Date.util';
import { ReceptionDTO, ReceptionSearchDTO } from 'models/reception';
import receptionService from 'services/reception.service';
import kpiService from 'services/kpi.service';
import { DateType, getReceptionStatus, getReceptionType, ReceptionItemKind } from 'enums/reception.enum';
import { RepairStatisticsKpiDTO } from 'models/kpi';
import ReceptionModal from 'components/_common/modal/ReceptionModal';

class Params {
    error: string | null = null;
    isLoaded = false;
    total: number = 0;

    list: ReceptionDTO[] = [];
    search: ReceptionSearchDTO = new ReceptionSearchDTO();

    kpiLoaded = false;
    kpiData: RepairStatisticsKpiDTO | null = null;

    statusSet = getReceptionStatus();
}

function RepairStatistics() {
    const [params, setParams] = useState(new Params());
    const [search, setSearch] = useState(false);

    // 모달 상태 관리 추가
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [editingRow, setEditingRow] = useState<ReceptionDTO | null>(null);

    useEffect(() => {
        const cur = PageUtil.deepCopy(params) as Params;
        // 기본: 이번달 1일 ~ 어제
        cur.search.dateType = DateType.RECEIVED;
        cur.search.startDate = null;
        cur.search.endDate = null;
        cur.isLoaded = true;
        setParams(cur);
        // setSearch(true);
        loadKPIData(params.search);
    }, []);

    useEffect(() => {
        if (!search) return;
        setSearch(false);
        getList(params.search);
        loadKPIData(params.search);
    }, [search]);

    async function getList(searchParams: ReceptionSearchDTO) {
        try {
            const res = await receptionService.list(searchParams);
            console.log("res => {}", res);
            setParams((prev) => ({
                ...prev,
                list: res.content || [],
                total: res.totalElements || 0,
                isLoaded: true,
                error: null,
            }));
        } catch (error) {
            setParams((prev) => ({
                ...prev,
                isLoaded: true,
                error: error as any,
            }));
            PageUtil.handleApiError(error);
        }
    }

    async function loadKPIData(search: ReceptionSearchDTO) {
        try {
            const kpiData = await kpiService.repairStatistics(search);
            console.log("repairStatistics KPI", kpiData);
            setParams(prev => ({
                ...prev,
                kpiLoaded: true,
                kpiData,
            }));
        } catch (error) {
            console.error('수리실적 KPI 조회 실패:', error);
            setParams(prev => ({
                ...prev,
                kpiLoaded: true,
            }));
        }
    }

    function onSearch() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.isLoaded = false;
        cur.search.page = 1;
        setParams(cur);
        setSearch(true);
    }

    function onClearFilter() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.search.dateType = DateType.RECEIVED;
        cur.search.types = "";
        cur.search.startDate = moment().startOf('month').format('YYYY-MM-DD');
        cur.search.endDate = moment().subtract(1, 'day').format('YYYY-MM-DD');
        cur.search.page = 1;
        cur.isLoaded = false;
        setParams(cur);
        setSearch(true);
    }

    function onClearManufactureMonthFilter() {
        const cur = PageUtil.deepCopy(params) as Params;
        cur.search.startManufactureMonth = null;
        cur.search.endManufactureMonth = null;
        cur.isLoaded = false;
        cur.search.page = 1;
        setParams(cur);
        setSearch(true);
    }

    /** 신규 접수 모달 열기 */
    function onOpenCreate() {
        setModalMode('create');
        setEditingRow(null);
        setShowModal(true);
    }

    /** 수정 모달 열기 */
    function onOpenEdit(row: ReceptionDTO) {
        setModalMode('edit');
        setEditingRow(row);
        setShowModal(true);
    }

    /** 모달에서 저장 완료 시 */
    async function onModalSave(reception: ReceptionDTO, close: boolean = true) {
        try {
            console.log("reception", reception);
            await receptionService.save(reception);
            if (close) setShowModal(false);
            setSearch(true); // 목록 새로고침
            alert('저장되었습니다.');
            // 필요시 목록 새로고침 등 추가 처리
        } catch (error) {
            console.error('접수 저장 실패:', error);
            alert('저장에 실패했습니다.');
        }
    }

    /** 모달 닫기 */
    function onModalClose() {
        setShowModal(false);
        setEditingRow(null);
    }

    const handleExportExcel = async () => {
        const cur = PageUtil.deepCopy(params) as Params;
        const searchParams = cur.search;
        searchParams.rows = 9999;
        searchParams.page = 1;
        const res = await receptionService.list(searchParams);
        const list = res.content || [];

        try {
            const excelData = list.map((row) => {
                const boilerName =
                    (row.items?.find(p => p.kind === ReceptionItemKind.BOILER)?.modelName || '') +
                    (row.items?.filter(p => p.kind === ReceptionItemKind.BOILER)?.length > 1 ? ' 外' : '');

                return {
                    '처리상황': getReceptionStatus().find(s => s.value === row.status)?.label || row.status || '',
                    '처리유형': getReceptionType().find(t => t.value === row.type)?.label || row.type || '',
                    '접수일자': row.receivedDate || '',
                    '연락처': row.phone1 || '',
                    '고객명': row.customerName || '',
                    '보일러명': boilerName,
                    '제조년월': row.items?.find(p => p.manufactureMonth)?.manufactureMonth || '',
                    '특이사항': row.items?.filter(p => p.note).map(p => p.note).join(', ') || '',
                    '수리내역': row.repairNote || '',
                    '접수내용': row.requestNote || '',
                    '처리내용': row.handleNote || '',
                };
            });

            const today = PageUtil.getCurrentDate();
            let filename = `수리실적_${today}`;

            if (searchParams.startDate && searchParams.endDate) {
                filename += `_${searchParams.startDate}부터${searchParams.endDate}까지`;
            }
            if (searchParams.keyword) {
                filename += `_${searchParams.keyword}`;
            }
            filename += '.xlsx';

            PageUtil.exportToExcel(excelData, filename, '수리실적');
        } catch (error) {
            console.error('엑셀 내보내기 오류:', error);
            alert('엑셀 내보내기 중 오류가 발생했습니다.');
        }
    };

    const args = {
        params,
        setParams,
        setSearch,
        handleChange: PageUtil.handleChange,
        onSearch,
        onClearFilter,
        onClearManufactureMonthFilter,
        onOpenCreate,
        onOpenEdit,
        onModalSave,
        onModalClose,
        handleExportExcel,
    };

    return (
        <>
            <RepairStatisticsV {...args} />

            {/* 접수 등록/수정 모달 */}
            <ReceptionModal
                show={showModal}
                title={modalMode === 'create' ? '신규 접수 등록' : '접수 수정'}
                initial={editingRow}
                onSave={onModalSave}
                onClose={onModalClose}
            />
        </>
    );
}

export default withRouter(RepairStatistics);